<script setup lang="ts">
// The `/lib` entry, not the root: the root build inlines a full copy of Vue for the
// standalone CDN flow, and a component from that copy crashes inside a host app with
// "Cannot read properties of null (reading 'isCE')" — its inlined renderer tracks its
// own currentRenderingInstance, which our Vue never sets. `/lib` imports vue instead.
import { MyParcelDeliveryOptions } from '@myparcel/delivery-options/lib';
import '@myparcel/delivery-options/style.css';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { formatSelectionMessage, type WidgetSelection } from '../lib/deliveryOptions';

/**
 * MyParcel's own delivery-options widget — the component webshops embed — mounted
 * directly as a Vue component rather than through the CDN snippet. Same library, but
 * no second copy of Vue (npm dedupes to the app's own 3.5) and no document-level
 * event bus with a global selector, which would break the moment a conversation
 * holds two of these.
 *
 * The widget fetches /delivery_options itself; that endpoint is public (verified: it
 * answers without credentials), so no API key is involved. Translations come from
 * assets.myparcel.nl at runtime, noted in docs/data-boundary.md.
 */

const props = defineProps<{
  filters: { cc: string; postalCode: string; number: string };
}>();

const emit = defineEmits<{ choose: [message: string, display: string] }>();

/**
 * Dutch interface strings. The widget renders raw keys ("deliveryTitle", "error3212")
 * unless strings are supplied: the translations live in MyParcel's standalone CDN app,
 * not in the published component. Copied verbatim from
 * apps/delivery-options/src/config/getDefaultStrings.ts in myparcelnl/delivery-options.
 */
const STRINGS = {
  cc: 'Land',
  city: 'Plaats',
  postalCode: 'Postcode',
  number: 'Huisnummer',
  street: 'Straat',
  addressNotFound: 'Adresgegevens niet ingevuld',
  closed: 'Gesloten',
  discount: 'Korting',
  from: 'Vanaf',
  showMoreLocations: 'Toon meer locaties',
  showMoreHours: 'Toon meer openingstijden',
  headerDeliveryOptions: '',
  compactDelivery: 'Thuisbezorgen',
  compactPickup: 'Afhalen op locatie',
  compactBackToOverview: 'Terug naar overzicht',
  popUpMapOpen: 'Open kaart',
  popUpMapConfirm: 'Bevestigen',
  popUpMapTitle: 'Afhaallocatie kiezen',
  deliveryTitle: 'Thuis of op het werk bezorgen',
  deliveryStandardTitle: 'Standaard bezorging',
  deliverySameDayTitle: 'Vandaag bezorgd',
  ecoFriendly: 'Meest milieuvriendelijk',
  noDeliveryOptionsAvailable: 'Geen bezorgopties beschikbaar',
  onlyRecipientTitle: 'Alleen ontvanger',
  parcelLocker: 'Pakketautomaat',
  pickUp: 'Afhalen',
  pickUpFrom: 'Afhalen vanaf',
  pickupTitle: 'Afhalen op locatie',
  priorityDeliveryTitle: 'Prioriteit bezorging',
  signatureTitle: 'Handtekening voor ontvangst',
  openingHours: 'Openingstijden',
  options: 'Opties',
  // v6.22.0 labels its list/map toggle with the raw view-enum values; the
  // *ListButton/*MapButton keys in the repo's main branch are newer than this build.
  list: 'Lijst',
  map: 'Kaart',
  packageTypeDigitalStamp: 'Digitale postzegel',
  packageTypeEnvelope: 'Envelop',
  packageTypeMailbox: 'Brievenbuspakje',
  error3212: '{} is verplicht.',
  error3224: '',
  error3501: 'Dit adres is niet gevonden.',
  error3505: 'Postcode is niet geldig voor het gekozen land.',
  error3506: '',
  error3728: '',
};

/**
 * Upstream bug shim. v6.22.0's request builder maps only cc, city, postal_code and
 * street onto /delivery_options — never AddressField.Number — while api.myparcel.nl
 * answers exactly that request with `{"code":3212,"message":"number is required"}` for
 * PostNL. It is not this integration: MyParcel's own sandbox shows "Geen bezorgopties
 * beschikbaar" for their own NL preset. Until that is fixed upstream, append the house
 * number to the widget's own requests. Scoped: installed while mounted, matches only
 * MyParcel delivery/pickup endpoints, and only adds `number` when it is absent.
 */
function installNumberShim(number: string): () => void {
  const original = window.fetch;

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const raw =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    if (/https:\/\/api\.myparcel\.nl\/(delivery_options|pickup_locations)\?/.test(raw)) {
      const url = new URL(raw);
      if (!url.searchParams.get('number')) {
        url.searchParams.set('number', number);
        return original(
          typeof input === 'string' || input instanceof URL ? url.toString() : new Request(url, input),
          init,
        );
      }
    }
    return original(input, init);
  };

  return () => {
    window.fetch = original;
  };
}

let removeShim: (() => void) | null = null;

onMounted(() => {
  removeShim = installNumberShim(props.filters.number);
});

onBeforeUnmount(() => {
  removeShim?.();
  removeShim = null;
});

const selection = ref<WidgetSelection | null>(null);
/** Set when the user sends a choice, so one card cannot flood the chat with picks. */
const sent = ref(false);

const configuration = computed(() => ({
  address: {
    cc: props.filters.cc,
    postalCode: props.filters.postalCode,
    number: props.filters.number,
    // Not part of our tool input; the NL/BE lookups key on postal code + number.
    street: '',
    city: '',
  },
  /**
   * Typed as required, but the runtime treats an empty carrier list as "use the
   * built-in defaults for this platform" — measured in the bundle: it checks
   * `carriers.length > 0` and otherwise loads its own MyParcel platform config.
   */
  platformConfig: { carriers: [] },
  strings: STRINGS,
  config: {
    platform: 'myparcel' as const,
    locale: 'nl-NL',
    currency: 'EUR',
    // Every carrier the account could use; the widget hides what the address does not
    // support. Ids match src/tools/carrier-capabilities.ts.
    carrierSettings: {
      postnl: { allowDeliveryOptions: true, allowPickupLocations: true },
      dhlforyou: { allowDeliveryOptions: true, allowPickupLocations: true },
      dpd: { allowDeliveryOptions: true, allowPickupLocations: true },
    },
  },
}));

function onUpdate(value: unknown): void {
  selection.value = value as WidgetSelection;
}

function choose(): void {
  if (!selection.value || sent.value) {
    return;
  }
  sent.value = true;
  const { message, display } = formatSelectionMessage(selection.value);
  emit('choose', message, display);
}
</script>

<template>
  <div class="widget">
    <MyParcelDeliveryOptions :configuration="configuration" @update="onUpdate" />

    <div class="widget__foot">
      <button class="btn btn--primary btn--small" type="button" :disabled="!selection || sent" @click="choose">
        {{ sent ? 'Keuze doorgegeven' : 'Gebruik deze bezorgkeuze' }}
      </button>
      <span v-if="!selection" class="widget__hint">Kies hierboven een bezorgmoment of ophaalpunt.</span>
    </div>
  </div>
</template>

<style scoped>
.widget {
  border-top: 1px solid var(--border);
  padding: 12px;
}

.widget__foot {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.widget__hint {
  font-size: 12px;
  color: var(--text-muted);
}
</style>
